import React from "react";
import { Link } from "react-router-dom";
import { Link2, TrendingUp, ArrowRight, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import LeadFormSticky from "@/components/site/LeadFormSticky";
import CalendlyButton from "@/components/site/CalendlyButton";
import { trackEvent } from "@/lib/analytics";
import api from "@/lib/api";

const categoryMap = {
  "mutual-fund": { label: "Mutual Funds", color: "text-sky-300 bg-sky-500/10 border-sky-500/20" },
  sip: { label: "SIPs", color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" },
  bond: { label: "Bonds", color: "text-amber-300 bg-amber-500/10 border-amber-500/20" },
  stock: { label: "Stocks", color: "text-rose-300 bg-rose-500/10 border-rose-500/20" },
};

export default function InvestmentsPage() {
  const [products, setProducts] = React.useState([]);

  React.useEffect(() => {
    api.get("/products").then((r) =>
      setProducts(r.data.filter((p) => ["mutual-fund", "sip", "bond", "stock"].includes(p.category)))
    );
  }, []);

  return (
    <div data-testid="investments-page">
      <LeadFormSticky source="investments" />
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,165,233,0.16),transparent_55%)]" />
        <div className="mfb-container relative max-w-4xl">
          <div className="overline mb-4">Investment Products</div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white leading-[1.05]">
            <span className="gradient-text-sky">AI-curated portfolios</span>, built for compounding.
          </h1>
          <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-2xl">
            Mutual funds, systematic investment plans, government bonds and global equities — all engineered into one premium account.
          </p>
          <div className="mt-8 flex gap-3">
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="invest-cta-open"
              onClick={() => trackEvent("open_account_click", { source: "investments" })}
            >
              Open Account <ArrowRight className="w-4 h-4" />
            </Link>
            <CalendlyButton label="Talk to Advisor" source="investments" testId="invest-cta-advisor" />
          </div>
        </div>
      </section>

      <section className="mfb-section">
        <div className="mfb-container">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => {
              const meta = categoryMap[p.category] || { label: p.category, color: "text-slate-300 bg-white/5 border-white/10" };
              return (
                <div key={p.id} className="card-luxury p-7 flex flex-col" data-testid={`product-${p.id}`}>
                  <Badge className={`${meta.color} border w-fit`}>{meta.label}</Badge>
                  <h3 className="mt-4 font-[Outfit] text-2xl text-white font-light">{p.name}</h3>
                  <p className="mt-2 text-slate-400 text-sm leading-relaxed flex-1">{p.description}</p>
                  <div className="mt-5 grid grid-cols-2 gap-3 pt-5 border-t border-white/5">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Return</div>
                      <div className="text-emerald-400 text-sm font-medium mt-0.5">{p.return_rate || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Risk</div>
                      <div className="text-white text-sm mt-0.5">{p.risk_level || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Min</div>
                      <div className="text-white text-sm mt-0.5">{p.min_investment || "—"}</div>
                    </div>
                  </div>
                  <Link to="/contact" className="mt-5 text-sky-400 text-sm flex items-center gap-1">
                    Get started <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container">
          <div className="overline mb-3">Why MFB Investments</div>
          <h2 className="font-[Outfit] text-4xl font-light text-white">Built for outperformance.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { t: "Zero account opening", d: "No setup fees. No annual maintenance. Just performance." },
              { t: "Ultra-low transaction cost", d: "Institutional pricing from 0.05% per trade." },
              { t: "Personalized AI baskets", d: "Tailored to your risk, horizon and goals." },
            ].map((x, i) => (
              <div key={i} className="card-luxury p-7">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                <div className="mt-4 text-white font-[Outfit] text-xl">{x.t}</div>
                <div className="mt-2 text-slate-400 text-sm leading-relaxed">{x.d}</div>
                <div className="mt-4 flex items-center gap-2 text-emerald-400 text-xs">
                  <Check className="w-3.5 h-3.5" /> Included with every account
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
